import type { WidgetType } from '@flexbyt/shared';
import { Logo, cx } from '@flexbyt/widget-preview';
import { Link, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { PRIMARY_NAV, type NavGroup, type NavLink } from '../../content/navigation.js';
import { LayoutSketch } from '../widgets/LayoutSketch.js';
import { WIDGET_CONTENT, widgetPath } from '../../content/widgets.js';
import { track } from '../../lib/analytics.js';
import { appUrl, startUrl } from '../../lib/site.js';

import { GUTTER } from '../ui/layout.js';

/**
 * The marketing navigation.
 *
 * ===========================================================================
 * Why the widget menu has pictures and the others do not
 * ===========================================================================
 * The brief says to use a mega menu only where the result is genuinely
 * cleaner, which is a question worth answering per menu rather than once.
 *
 * "Product → Import / Manage / Display" is three words that mean exactly what
 * they say. A panel of illustrations there would be decoration.
 *
 * "Widgets → Carousel / Masonry / Marquee / Grid / List / Popup" is six words
 * that mean almost nothing on their own. The entire difference between Masonry
 * and Grid is a shape, and no sentence explains it faster than showing it. So
 * that menu draws each layout — with `LayoutSketch`, the same drawing the
 * builder's layout picker and the integration pages use, so every surface on
 * this site agrees about what "masonry" looks like.
 *
 * ===========================================================================
 * Keyboard and screen readers
 * ===========================================================================
 * The triggers are `<button aria-expanded aria-controls>` inside a `<nav>`,
 * which is the disclosure pattern — not the menubar pattern. `role="menu"` is
 * for application menus and brings an obligation to implement arrow-key
 * roving focus, type-ahead and a focus trap; a list of links to pages is not
 * that, and a half-implemented `role="menu"` is worse for a screen-reader user
 * than plain markup, because it promises behaviour that is not there.
 *
 * So: Tab moves through triggers and links in document order, Enter and Space
 * open, Escape closes and returns focus to the trigger, and hover opens on a
 * pointer as a convenience on top of all of it.
 */

const HOVER_CLOSE_DELAY = 120;

export function SiteHeader() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Navigating closes everything. Without this, following a link from a menu
  // leaves the panel hanging over the page that just loaded.
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [pathname]);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  /*
    A short delay before closing on pointer-out.

    The gap between a trigger and its panel is a few pixels of nothing, and a
    menu that closes the instant the cursor crosses it is a menu you have to
    approach in a straight line. The delay is short enough not to feel sticky
    and long enough to survive an ordinary diagonal move.
  */
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenMenu(null), HOVER_CLOSE_DELAY);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  /*
    Glass that reacts to what is under it, as on flexbyt.com: nearly clear at
    the top of the page, frosted once scrolled, and dark glass while it floats
    over a night band or the footer. Sampled at the header's own centre line,
    once per frame at most.
  */
  const [scrolled, setScrolled] = useState(false);
  const [dark, setDark] = useState(false);
  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      setScrolled(window.scrollY > 4);
      // Not every environment implements hit-testing (jsdom does not).
      if (typeof document.elementsFromPoint !== 'function') return;
      const below = document
        .elementsFromPoint(window.innerWidth / 2, 36)
        .find((el) => !el.closest('header'));
      setDark(Boolean(below?.closest('[data-nav-theme="dark"], footer')));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [pathname]);

  // The soft pill that slides between items on hover.
  const menuRef = useRef<HTMLElement>(null);
  const [pill, setPill] = useState({ left: 0, width: 0, visible: false });
  const movePill = (event: React.PointerEvent<HTMLElement>) => {
    const menu = menuRef.current?.getBoundingClientRect();
    if (!menu) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setPill({ left: rect.left - menu.left, width: rect.width, visible: true });
  };

  const onDark = dark && !mobileOpen;

  // Escape closes the open menu wherever focus is, and a click outside the nav
  // dismisses it — both are what everybody expects of a thing overlaying a page.
  useEffect(() => {
    if (!openMenu) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpenMenu(null);
    }
    function onPointerDown(event: PointerEvent) {
      if (!navRef.current?.contains(event.target as Node)) setOpenMenu(null);
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [openMenu]);

  return (
    <header
      className={cx(
        'sticky top-0 z-50 border-b backdrop-blur-2xl backdrop-saturate-[1.8]',
        'transition-[background-color,border-color,box-shadow] duration-300',
        onDark
          ? 'border-white/[0.08] bg-night/60 shadow-[inset_0_-1px_0_rgba(255,255,255,0.04)]'
          : scrolled || mobileOpen
            ? 'border-black/[0.06] bg-white/70 shadow-[0_8px_32px_-16px_rgba(10,10,11,0.18)]'
            : 'border-transparent bg-white/40'
      )}
    >
      <div
        ref={navRef}
        className={cx('mx-auto flex h-[4.5rem] max-w-wide items-center gap-6', GUTTER)}
      >
        <Link
          to="/"
          className={cx(
            'shrink-0 rounded-sm transition-colors duration-300',
            onDark ? 'text-white' : 'text-ink-950'
          )}
          aria-label="ReviewsJet — home"
        >
          <Logo size="sm" className="h-7" />
        </Link>

        {/* Desktop navigation */}
        <nav
          ref={menuRef}
          aria-label="Main"
          onPointerLeave={() => setPill((p) => ({ ...p, visible: false }))}
          className="relative hidden lg:mx-auto lg:flex lg:items-center lg:gap-1"
        >
          <span
            aria-hidden
            className={cx(
              'pointer-events-none absolute top-0 left-0 h-full rounded-full',
              'transition-[transform,width,opacity,background-color] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
              onDark ? 'bg-white/10' : 'bg-black/[0.05]'
            )}
            style={{
              width: pill.width,
              transform: `translateX(${pill.left}px)`,
              opacity: pill.visible ? 1 : 0,
            }}
          />
          {PRIMARY_NAV.map((group) =>
            group.links ? (
              <DesktopMenu
                key={group.label}
                dark={onDark}
                onHover={movePill}
                group={group}
                open={openMenu === group.label}
                onOpen={() => {
                  cancelClose();
                  setOpenMenu(group.label);
                }}
                onClose={() => setOpenMenu(null)}
                onScheduleClose={scheduleClose}
                onCancelClose={cancelClose}
              />
            ) : (
              <Link
                key={group.label}
                to={group.to!}
                onPointerEnter={movePill}
                className={cx(
                  'relative rounded-full px-4 py-2 text-[15px] font-semibold',
                  'transition-colors duration-[var(--duration-fast)]',
                  onDark ? 'text-white/70 hover:text-white' : 'text-ink-600 hover:text-ink-950'
                )}
                activeProps={{ className: onDark ? 'text-white' : 'text-ink-950' }}
              >
                {group.label}
              </Link>
            )
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <a
            href={appUrl('/login')}
            className={cx(
              'hidden rounded-full px-4 py-2 text-[15px] font-semibold transition-colors sm:inline-flex',
              onDark ? 'text-white/70 hover:text-white' : 'text-ink-600 hover:text-ink-950'
            )}
          >
            Log in
          </a>

          {/* flexbyt.com's call to action: a pill with its arrow in a chip. */}
          <a
            href={startUrl('header')}
            onClick={() => track('hero_cta_clicked', { surface: 'header' })}
            className={cx(
              'group inline-flex h-11 shrink-0 items-center gap-2 rounded-full pr-1.5 pl-5 text-[15px] font-semibold transition-colors',
              onDark
                ? 'bg-white text-ink-950 hover:bg-white/90'
                : 'bg-ink-950 text-white hover:bg-ink-800'
            )}
          >
            Start free
            <span
              aria-hidden
              className={cx(
                'flex size-8 items-center justify-center rounded-full transition-transform group-hover:translate-x-0.5',
                onDark ? 'bg-ink-950/10' : 'bg-white/15'
              )}
            >
              <ArrowRight />
            </span>
          </a>

          <MobileToggle
            open={mobileOpen}
            dark={onDark}
            onToggle={() => setMobileOpen((v) => !v)}
          />
        </div>
      </div>

      {mobileOpen ? <MobileNav onNavigate={() => setMobileOpen(false)} /> : null}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Desktop
// ---------------------------------------------------------------------------

function DesktopMenu({
  group,
  dark,
  onHover,
  open,
  onOpen,
  onClose,
  onScheduleClose,
  onCancelClose,
}: {
  group: NavGroup;
  dark: boolean;
  onHover: (event: React.PointerEvent<HTMLElement>) => void;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onScheduleClose: () => void;
  onCancelClose: () => void;
}) {
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  /*
    Is the pointer currently over this group?

    It decides what a click means, and getting that wrong is a bug you cannot
    see in a screenshot. `pointerenter` fires before `click`, so a trigger that
    simply toggles closes the menu that hovering just opened — every click on a
    desktop menu shuts it, intermittently enough to look like a flake.

    So: with the pointer over the trigger, a click *opens* and never closes,
    because hover has already expressed the intent and closing is what moving
    away is for. Reached by keyboard — where no pointer event has fired — a
    click toggles, which is what Enter on a disclosure button should do.
  */
  const hovering = useRef(false);

  return (
    <div
      className="relative"
      onPointerEnter={(event) => {
        hovering.current = true;
        onHover(event);
        onOpen();
      }}
      onPointerLeave={() => {
        hovering.current = false;
        onScheduleClose();
      }}
      /*
        Focus leaving the whole group closes it. `focusout` and not `blur`,
        because blur does not bubble — so tabbing from the last link in the
        panel to the next trigger would leave the panel open behind it.
      */
      onFocusCapture={onCancelClose}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) onClose();
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          if (hovering.current) onOpen();
          else if (open) onClose();
          else onOpen();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            onClose();
            triggerRef.current?.focus();
          }
        }}
        className={cx(
          'relative flex items-center gap-1 rounded-full px-4 py-2 text-[15px] font-semibold',
          'transition-colors duration-[var(--duration-fast)]',
          dark
            ? open
              ? 'text-white'
              : 'text-white/70 hover:text-white'
            : open
              ? 'text-ink-950'
              : 'text-ink-600 hover:text-ink-950'
        )}
      >
        {group.label}
        <svg
          viewBox="0 0 20 20"
          aria-hidden
          className={cx(
            'size-3.5 opacity-60 transition-transform duration-[var(--duration-fast)]',
            open && 'rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m5.2 7.8 4.8 4.8 4.8-4.8" />
        </svg>
      </button>

      {open ? (
        <div
          id={panelId}
          className={cx(
            'rj-panel-in absolute top-full left-1/2 z-50 -translate-x-1/2 pt-3',
            group.visual ? 'w-[32rem]' : 'w-80'
          )}
        >
          <div className="rounded-[1.5rem] border border-black/[0.07] bg-white p-2 shadow-[0_30px_70px_-20px_rgba(10,10,11,0.3)]">
          {group.visual ? (
            <ul className="grid grid-cols-3 gap-1">
              {group.links!.map((link) => (
                <li key={link.to}>
                  <WidgetMenuItem link={link} />
                </li>
              ))}
            </ul>
          ) : (
            <ul className="grid gap-0.5">
              {group.links!.map((link) => (
                <li key={link.to}>
                  <MenuItem link={link} />
                </li>
              ))}
            </ul>
          )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({ link }: { link: NavLink }) {
  return (
    <Link
      to={link.to}
      className={cx(
        'block rounded-2xl px-4 py-3',
        'transition-colors duration-[var(--duration-fast)] hover:bg-surface'
      )}
    >
      <span className="block text-[15px] font-bold text-ink-950">{link.label}</span>
      {link.description ? (
        <span className="mt-0.5 block text-caption text-ink-500">{link.description}</span>
      ) : null}
    </Link>
  );
}

/**
 * A widget menu item: the layout's own sketch, with its name beneath.
 *
 * ---------------------------------------------------------------------------
 * The sketch is the content; the sentence is not
 * ---------------------------------------------------------------------------
 * This started as a two-column list with a thumbnail and a two-line
 * description each, and it was worse than the plain text menu it replaced —
 * eighteen lines of prose to choose between six shapes, in a panel you are
 * meant to be passing through.
 *
 * A menu answers "which one do I want". For layouts the answer is entirely
 * visual, so the picture is given the room and the explaining is left to the
 * page it links to. Three across rather than two, sketch above the name rather
 * than beside it — which also gives each sketch enough width to actually read
 * as an arrangement. At 80px the masonry columns collapsed and overflowed into
 * the row below it, which is a good demonstration of the general rule that a
 * thumbnail too small to identify is not a small thumbnail, it is noise.
 *
 * `LayoutSketch` is the site's drawing of each layout, shared with the widget
 * builder's picker, so this menu and the builder can never disagree about
 * what "masonry" looks like.
 */
function WidgetMenuItem({ link }: { link: NavLink }) {
  const type = WIDGET_CONTENT.find((widget) => widgetPath(widget) === link.to)
    ?.type as WidgetType | undefined;

  return (
    <Link
      to={link.to}
      className={cx(
        'block rounded-2xl p-2.5',
        'transition-colors duration-[var(--duration-fast)] hover:bg-surface'
      )}
    >
      {type ? <LayoutSketch type={type} className="h-[84px]" /> : null}
      <span className="mt-2 block text-body-sm font-bold text-ink-950">
        {link.label}
      </span>
    </Link>
  );
}

function ArrowRight() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-3.5"
    >
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Mobile
// ---------------------------------------------------------------------------

function MobileToggle({
  open,
  dark,
  onToggle,
}: {
  open: boolean;
  dark: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls="mobile-nav"
      aria-label={open ? 'Close menu' : 'Open menu'}
      className={cx(
        'grid size-11 place-items-center rounded-full lg:hidden',
        'transition-colors duration-[var(--duration-fast)]',
        dark ? 'text-white hover:bg-white/10' : 'text-ink-700 hover:bg-black/[0.05] hover:text-ink-950'
      )}
    >
      {/*
        Two bars that become a cross. The transform is the only "clever" motion
        on the site and it earns its place: it is the same two objects moving,
        so it shows that the button toggles one thing rather than swapping
        between two icons.
      */}
      <span className="relative block h-3.5 w-5" aria-hidden>
        <span
          className={cx(
            'absolute left-0 block h-[1.5px] w-5 rounded-full bg-current',
            'transition-transform duration-[var(--duration-base)] [transition-timing-function:var(--ease-out-soft)]',
            open ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0'
          )}
        />
        <span
          className={cx(
            'absolute left-0 block h-[1.5px] w-5 rounded-full bg-current',
            'transition-transform duration-[var(--duration-base)] [transition-timing-function:var(--ease-out-soft)]',
            open ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'top-full -translate-y-full'
          )}
        />
      </span>
    </button>
  );
}

/**
 * The mobile navigation.
 *
 * A full-height panel below the header rather than a slide-in drawer from the
 * side. On a phone the header is already pinned to the top and the thumb is at
 * the bottom — a panel that drops from the thing you just tapped is where the
 * eye already is, and it needs no scrim, no focus trap and no body-scroll lock
 * to behave correctly.
 *
 * Sections are `<details>`. Native disclosure means the open/closed state, the
 * keyboard handling and the screen-reader announcement are the platform's,
 * correct in every browser, and working before any JavaScript has run — which
 * on the slow connections where a marketing site is won is not a small thing.
 */
function MobileNav({ onNavigate }: { onNavigate: () => void }) {
  return (
    <div
      id="mobile-nav"
      className={cx(
        'rj-panel-in absolute inset-x-0 top-[4.5rem] z-40 max-h-[calc(100dvh-4.5rem)] overflow-y-auto',
        'border-b border-ink-150 bg-white lg:hidden'
      )}
    >
      <nav aria-label="Main" className={cx('py-3', GUTTER)}>
        <ul className="divide-y divide-ink-100">
          {PRIMARY_NAV.map((group) => (
            <li key={group.label} className="py-1">
              {group.links ? (
                <details className="group">
                  <summary
                    className={cx(
                      'flex cursor-pointer list-none items-center justify-between rounded-lg px-1 py-3',
                      'text-body font-medium text-ink-950',
                      // Safari draws its own triangle on a summary unless this
                      // pseudo-element is cleared as well.
                      '[&::-webkit-details-marker]:hidden'
                    )}
                  >
                    {group.label}
                    <svg
                      viewBox="0 0 20 20"
                      aria-hidden
                      className="size-4 text-ink-400 transition-transform duration-[var(--duration-fast)] group-open:rotate-180"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m5.2 7.8 4.8 4.8 4.8-4.8" />
                    </svg>
                  </summary>

                  <ul className="pb-2">
                    {group.links.map((link) => (
                      <li key={link.to}>
                        <Link
                          to={link.to}
                          onClick={onNavigate}
                          className="block rounded-lg px-1 py-2.5 text-body text-ink-600 hover:text-ink-950"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : (
                <Link
                  to={group.to!}
                  onClick={onNavigate}
                  className="block rounded-lg px-1 py-3 text-body font-medium text-ink-950"
                >
                  {group.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        <a
          href={appUrl('/login')}
          className="mt-4 block rounded-lg px-1 py-3 text-body font-medium text-ink-950 sm:hidden"
        >
          Log in
        </a>
      </nav>
    </div>
  );
}
